import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

interface Inquiry {
  id: string;
  name: string;
  phone: string;
  subject: string;
  message: string;
  admin_reply: string | null;
  replied_at: string | null;
  is_public: boolean;
  created_at: string;
}

export default function AdminInquiriesPage() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
  const [replyText, setReplyText] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filter, setFilter] = useState<'all' | 'pending' | 'replied'>('all');
  const [mobileView, setMobileView] = useState<'list' | 'detail'>('list');

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('inquiries')
      .select('*')
      .order('created_at', { ascending: false });

    if (!error && data) {
      setInquiries(data);
    }
    setLoading(false);
  };

  const handleSelectInquiry = (inquiry: Inquiry) => {
    setSelectedInquiry(inquiry);
    setReplyText(inquiry.admin_reply || '');
    setIsPublic(inquiry.is_public);
    setMobileView('detail');
  };

  const handleBackToList = () => {
    setMobileView('list');
    setSelectedInquiry(null);
  };

  const handleSubmitReply = async () => {
    if (!selectedInquiry || !replyText.trim()) return;

    setIsSubmitting(true);

    const { error } = await supabase
      .from('inquiries')
      .update({
        admin_reply: replyText,
        replied_at: new Date().toISOString(),
        is_public: isPublic
      })
      .eq('id', selectedInquiry.id);

    setIsSubmitting(false);

    if (error) {
      alert('답변 등록에 실패했습니다.');
      return;
    }

    alert('답변이 등록되었습니다.');
    fetchInquiries();
    setSelectedInquiry(null);
    setReplyText('');
    setMobileView('list');
  };

  const handleDeleteInquiry = async (id: string) => {
    if (!confirm('정말 삭제하시겠습니까?')) return;

    const { error } = await supabase
      .from('inquiries')
      .delete()
      .eq('id', id);

    if (error) {
      alert('삭제에 실패했습니다.');
      return;
    }

    fetchInquiries();
    if (selectedInquiry?.id === id) {
      setSelectedInquiry(null);
      setMobileView('list');
    }
  };

  const handleLogout = async () => {
    if (confirm('로그아웃 하시겠습니까?')) {
      await signOut();
      navigate('/admin/login');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredInquiries = inquiries.filter(inquiry => {
    if (filter === 'pending') return !inquiry.admin_reply;
    if (filter === 'replied') return !!inquiry.admin_reply;
    return true;
  });

  const pendingCount = inquiries.filter(i => !i.admin_reply).length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => mobileView === 'detail' ? handleBackToList() : navigate('/')}
              className="mr-2 sm:mr-4 w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl text-gray-600"></i>
            </button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-gray-900">
                {mobileView === 'detail' && selectedInquiry ? '답변 작성' : '문의 관리'}
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">
                {pendingCount > 0 ? `${pendingCount}개의 답변 대기 중` : '모든 문의에 답변 완료'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchInquiries}
              className="px-3 sm:px-4 py-2 text-sm font-medium text-teal-600 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line sm:mr-1"></i>
              <span className="hidden sm:inline">새로고침</span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-logout-box-r-line"></i>
              <span className="hidden sm:inline">로그아웃</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-4 sm:py-6">
        {/* Filter Tabs - Only show on list view for mobile */}
        <div className={`flex flex-wrap gap-2 mb-4 sm:mb-6 ${mobileView === 'detail' ? 'hidden sm:flex' : ''}`}>
          <button
            onClick={() => setFilter('all')}
            className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              filter === 'all'
                ? 'bg-teal-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            전체 ({inquiries.length})
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              filter === 'pending'
                ? 'bg-yellow-500 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            대기중 ({pendingCount})
          </button>
          <button
            onClick={() => setFilter('replied')}
            className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              filter === 'replied'
                ? 'bg-green-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            답변완료 ({inquiries.length - pendingCount})
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Inquiry List - Hidden on mobile when viewing detail */}
          <div className={`bg-white rounded-xl shadow-sm overflow-hidden ${mobileView === 'detail' ? 'hidden lg:block' : ''}`}>
            <div className="p-3 sm:p-4 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900 text-sm sm:text-base">문의 목록</h2>
            </div>

            {loading ? (
              <div className="p-8 text-center">
                <i className="ri-loader-4-line animate-spin text-3xl text-gray-400"></i>
              </div>
            ) : filteredInquiries.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">
                문의가 없습니다.
              </div>
            ) : (
              <div className="divide-y divide-gray-100 max-h-[500px] sm:max-h-[600px] overflow-y-auto">
                {filteredInquiries.map((inquiry) => (
                  <div
                    key={inquiry.id}
                    onClick={() => handleSelectInquiry(inquiry)}
                    className={`p-3 sm:p-4 cursor-pointer transition-colors ${
                      selectedInquiry?.id === inquiry.id
                        ? 'bg-teal-50'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center flex-wrap gap-1 sm:gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          inquiry.admin_reply
                            ? 'bg-green-100 text-green-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {inquiry.admin_reply ? '답변완료' : '대기중'}
                        </span>
                        {inquiry.is_public && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-700">
                            공개
                          </span>
                        )}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteInquiry(inquiry.id);
                        }}
                        className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors cursor-pointer"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                    <h3 className="font-medium text-gray-900 mb-1 line-clamp-1 text-sm sm:text-base">
                      {inquiry.subject}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-500 line-clamp-2 mb-2">
                      {inquiry.message}
                    </p>
                    <div className="flex items-center flex-wrap text-xs text-gray-400">
                      <span>{inquiry.name}</span>
                      {inquiry.phone && (
                        <>
                          <span className="mx-1">·</span>
                          <span>{inquiry.phone}</span>
                        </>
                      )}
                      <span className="mx-1">·</span>
                      <span>{formatDate(inquiry.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Reply Panel - Full width on mobile when viewing detail */}
          <div className={`bg-white rounded-xl shadow-sm overflow-hidden ${mobileView === 'list' ? 'hidden lg:block' : ''}`}>
            <div className="p-3 sm:p-4 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900 text-sm sm:text-base">답변 작성</h2>
            </div>

            {selectedInquiry ? (
              <div className="p-3 sm:p-4">
                {/* Original Inquiry */}
                <div className="mb-4 sm:mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{selectedInquiry.subject}</h3>
                    <span className="text-xs text-gray-400">
                      {formatDate(selectedInquiry.created_at)}
                    </span>
                  </div>
                  <div className="p-3 sm:p-4 bg-gray-50 rounded-lg mb-3">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {selectedInquiry.message}
                    </p>
                  </div>
                  <div className="flex items-center flex-wrap text-xs sm:text-sm text-gray-500">
                    <i className="ri-user-line mr-1"></i>
                    <span>{selectedInquiry.name}</span>
                    {selectedInquiry.phone && (
                      <>
                        <span className="mx-2">·</span>
                        <i className="ri-phone-line mr-1"></i>
                        <span>{selectedInquiry.phone}</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Reply Form */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    답변 내용
                  </label>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value.slice(0, 500))}
                    placeholder="답변을 작성해주세요..."
                    rows={5}
                    maxLength={500}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm resize-none"
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">{replyText.length}/500</p>

                  <div className="flex items-center mt-4 mb-4 sm:mb-6">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isPublic}
                        onChange={(e) => setIsPublic(e.target.checked)}
                        className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                      />
                      <span className="ml-2 text-xs sm:text-sm text-gray-700">
                        홈페이지에 공개
                      </span>
                    </label>
                  </div>

                  <button
                    onClick={handleSubmitReply}
                    disabled={isSubmitting || !replyText.trim()}
                    className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        저장 중...
                      </span>
                    ) : (
                      '답변 저장'
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-chat-check-line text-gray-400 text-2xl"></i>
                </div>
                <p className="text-sm">좌측에서 문의를 선택해주세요</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
